import Component from '@component';
import {debounce} from 'lodash';
import {render, html} from 'lit-html';
import Device from '@utils/html/Device';
import {breakpointsApp} from '../colorz/configClrzApp';

export default class FaqPage extends Component {
	static selectors = {
		el: '[data-component="faq-page"]',
		questionContainer: '[data-component="expandable"]',
		questionBtn: '[data-expandable-btn]',
		questionWrapper: '[data-expandable-wrapper]',
	};
	onInit(el) {
		this.el = el;
		// Sélectionner l'élément avec l'attribut data-faq-json
		this.faqDataJson = this.el.dataset.faqJson;
		this.faqDataRichtextJson = this.el.querySelector(
			'[data-faq-richtext-json]',
		).textContent;

		this.searchInput = this.el.querySelector('[data-faq-input]');

		this.container = this.el.querySelector('[data-container]');
		this.categoryWrapper = this.el.querySelector('[data-category-wrapper]');
		this.categoryContainer = this.el.querySelector('[data-category-container]');
		this.faqContainer = this.el.querySelector('[data-faq-container]');
		this.resultsMessage = this.el.dataset.resultsMessage;

		this.categories = [];
		this.faqs = [];
		this.currentCategory = null;
		this.searchTerms = null;
		this.searchResults = [];

		// Initialise this.faqData en tableau vide pour stocker les données de FAQ
		this.faqData = [];

		// ------Init FAQ data-------------------------------
		// Vérifier si la valeur est valide

		this.initFaqJson();

		// ------End Init FAQ data-------------------------------

		// Search
		this.observeSearchInput();

		// Set initial category:
		this.currentCategory = this.categories[0];
		this.isMobile = Device.width < breakpointsApp.desktop;
	}

	onReady() {
		this.isMobile = Device.width < breakpointsApp.desktop;

		// Render category markup
		this.renderCategories();

		// Render category markup
		this.renderFAQs();

		// Only the first category is display
		this.onReadyShowOnlyFirstCategory();

		this.urlOpensQuestionCategory();
	}

	/*
	 * Render Methods
	 ***********/

	observeSearchInput() {
		this.searchInput.addEventListener(
			'input',
			debounce(() => {
				this.isSearching();
			}, 200),
		);
	}

	initFaqJson() {
		if (this.faqDataRichtextJson) {
			// Parser la chaîne JSON en tant qu'objet JavaScript
			const faqData = JSON.parse(this.faqDataRichtextJson);

			// Initialisez this.faqData comme un tableau vide pour stocker les données de FAQ
			this.faqData = [];

			// Vérifiez si faqData.faqs est un tableau
			if (Array.isArray(faqData.faqs)) {
				// Parcourir chaque élément de FAQ dans l'objet faqData et le stocker dans this.faqData sous forme d'objet
				faqData.faqs.forEach((faqItem) => {
					const category = faqItem.category;
					const question = faqItem.question;
					const answer = faqItem.answer;
					this.faqData.push({
						category,
						question,
						answer,
					});
				});
			} else {
				console.error(
					'Invalid format for FAQ data. Expected an array of FAQs.',
				);
			}
		} else {
			console.error('Invalid value for data-faq-json attribute.');
		}
	}

	renderCategories() {
		// Get the reviews from the data object
		const categories = this.faqData;

		// Vide le contenu de categoryContainer avant d'itérer sur faqData
		this.categoryContainer.innerHTML = '';
		const addedCategories = new Set();

		// Séparer la catégorie "Your account" des autres catégories
		const yourAccountCategory = categories.filter(
			(faqItem) => faqItem.category === 'Your account',
		);
		const otherCategories = categories.filter(
			(faqItem) => faqItem.category !== 'Your account',
		);

		// Combine "your-account" category with other categories
		const sortedCategories = [...yourAccountCategory, ...otherCategories];

		sortedCategories.forEach((faqItem, index, active = false) => {
			const {category} = faqItem;

			// Check if the category has already been added
			if (!addedCategories.has(category)) {
				// Add the category to the set of added categories
				addedCategories.add(category);

				// Crée un élément de catégorie pour chaque élément de faqData
				const categoryElement = document.createElement('li');
				categoryElement.classList.add('u-cursor-pointer');

				// Navigation au clavier
				categoryElement.setAttribute('tabindex', '0');
				categoryElement.setAttribute('data-clickable', 'true');
				categoryElement.addEventListener('keydown', (event) => {
					if (event.key === 'Enter') {
						categoryElement.click();
					}
				});

				if (index == 0) {
					categoryElement.classList.add(
						'faq-page__category--active',
						't-link',
						'u-w-fit',
					);
				}

				categoryElement.setAttribute(
					'data-category',
					category.replaceAll(' ', '-').toLowerCase(),
				);
				categoryElement.setAttribute('tabindex', '0');
				categoryElement.innerHTML = `
                <span href="${category
									.replaceAll(' ', '-')
									.toLowerCase()}" class="faq-page__category-title t-text t-text--14 ">${category}</span>
            `;

				categoryElement.addEventListener('click', () => {
					this.clickOnActiveCategory(categoryElement);
				});

				document.body.addEventListener('keydown', (e) => {
					if (document.activeElement === categoryElement) {
						if (e.key === 'Enter' || e.keyCode === 13) {
							this.clickOnActiveCategory(categoryElement);
						}
					}
				});

				// Ajoute l'élément de catégorie à categoryContainer
				this.categoryContainer.appendChild(categoryElement);
			}
		});

		if (this.isMobile) {
			this.managedCategoriesContainerMobile();
		}
	}

	managedCategoriesContainerMobile() {
		if (!this.categoryContainer) {
			console.error('Category container not found.');
			return;
		}
		// Ajouter le SVG à categoryContainer en mode mobile
		const svgHTML = `
					<svg class="lg:u-hidden max-lg:u-absolute max-lg:u-top-20  max-lg:u-right-20 u-transition-all u-duration-300" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M18 9L11.9341 15L6 9" stroke="#004D9D" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
			`;

		this.categoryContainer.insertAdjacentHTML('afterend', svgHTML);
		// Sélectionner le SVG inséré
		const svgElement = this.categoryContainer.nextElementSibling;

		this.categoryWrapper.addEventListener('click', () => {
			this.categoryContainer.classList.toggle('!u-h-auto');
			svgElement.classList.toggle('u-rotate-180');
		});

		const catLiMobile = this.categoryContainer.querySelectorAll('li');

		catLiMobile.forEach((element) => {
			element.addEventListener('click', function () {
				var ul = this.parentNode;
				// Déplac l'élément li cliqué en tête de list
				ul.insertBefore(this, ul.firstChild);
			});
		});
	}

	renderFAQs() {
		// Get the reviews from the data object
		const questions = this.faqData;

		// Vide le contenu de categoryContainer avant d'itérer sur faqData
		this.faqContainer.innerHTML = '';

		questions.forEach((questionItem, i) => {
			const {question, answer, category} = questionItem;

			// Crée un élément de catégorie pour chaque élément de faqData
			const faqElement = document.createElement('li');
			faqElement.classList.add(
				'faq-page__question',
				'u-mb-20',
				'u-transition-all',
				'u-duration-300',
			);
			faqElement.setAttribute('data-question-answer', `${question} ${answer}`);
			faqElement.setAttribute('data-question', question);
			faqElement.setAttribute(
				'data-category',
				category.replaceAll(' ', '-').toLowerCase(),
			);
			faqElement.innerHTML = `
        <article
          class='c-faq-item u-relative | u-border u-border-blue | u-text-blue | u-group | js-expandable'
          data-component='faq-item'
          data-faq-item='${i}'
          id=${category.replaceAll(' ', '-').toLowerCase()}
        >
          <button
						id='expandable-btn-${i}'
            class='c-expandable__btn | u-px-20 u-py-20 lg:u-py-30 | u-flex u-justify-between u-space-x-10 | u-w-full | u-transition-all u-duration-300 | u-text-left t-button-focus'
            data-expandable-btn
            aria-controls='faq-item-${i}'
            aria-expanded='false'
			tabindex='-1'
			role='button'
          >
            <span class='t-text u-font-semibold'>
            ${question}
            </span>
						<div class="accordion__icon | u-flex u-items-center">
							<div id="${this.toUrlParameter(
								question,
							)}" class="accordion__icon accordion__icon--arrow | u-relative" data-expandable-btn-link data-link-container>
								<div
									id='copyMessage'
									data-link-message
									style='display: none;'
									class='u-absolute u-bottom-20 -u-left-1/2 lg:u-left-1/2 u-z-10 -u-translate-x-1/2 | u-p-10 | u-w-max u-bg-white | t-text t-text--12 u-text-blue'
								>
									Link copied to clipboard!
								</div>
								<svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M13.9812 1.71706L13.4704 1.26474C12.9144 0.77162 12.1988 0.5 11.4552 0.5C10.5849 0.5 9.75558 0.872787 9.17979 1.52287L7.71797 3.17392C7.52972 3.38613 7.43539 3.65914 7.45254 3.94243C7.46971 4.22531 7.59567 4.48486 7.8077 4.67303L7.94455 4.79463C8.13878 4.96728 8.38907 5.0623 8.64889 5.0623L8.71223 5.06048C8.99487 5.04348 9.25438 4.91737 9.443 4.70485L10.9052 3.05362C11.1624 2.76302 11.6504 2.73382 11.9413 2.99109L12.4518 3.4444C12.5983 3.57411 12.6853 3.75367 12.6971 3.95005C12.709 4.14627 12.644 4.33475 12.5141 4.48113L9.31633 8.09148C9.10104 8.33489 8.71883 8.40517 8.4303 8.25635C7.98081 8.02429 7.39484 8.13506 7.06145 8.51115L7.03638 8.53973C6.80286 8.80261 6.7056 9.15197 6.76891 9.49967C6.83286 9.84527 7.04788 10.1416 7.3595 10.3049C7.79077 10.5305 8.27726 10.6545 8.76625 10.6545H8.76662C9.6365 10.6545 10.4662 10.2768 11.0423 9.62658L14.2397 6.01425C15.351 4.7592 15.2356 2.83045 13.9812 1.71706Z" fill="#004D9D"/>
									<path d="M7.05746 11.2169C6.63596 10.8434 5.93424 10.8853 5.55965 11.3076L4.09817 12.9583C3.84094 13.2488 3.35321 13.2789 3.06271 13.0213L2.55158 12.5675C2.4052 12.4377 2.31815 12.2581 2.30628 12.0619C2.29435 11.866 2.35938 11.6776 2.48929 11.5312L5.68669 7.92108C5.89872 7.68143 6.27402 7.60903 6.55925 7.7493C7.02551 7.97916 7.61873 7.86713 7.97922 7.4587C8.20842 7.20026 8.30469 6.857 8.24398 6.51854C8.18366 6.17809 7.97426 5.88928 7.66988 5.72642C7.23199 5.49174 6.73637 5.36768 6.23708 5.36768C5.3668 5.36768 4.53748 5.74062 3.96169 6.3908L0.763883 10.0003C-0.34811 11.2553 -0.232353 13.1816 1.0217 14.2946L1.53246 14.7415C2.08815 15.2347 2.80378 15.5002 3.54741 15.5002C3.54775 15.5002 3.54741 15.5002 3.54775 15.5002C4.41802 15.5002 5.24769 15.1333 5.82379 14.4831L7.28502 12.8356C7.47361 12.6231 7.56797 12.3514 7.55076 12.0697C7.53433 11.7898 7.40473 11.5236 7.19498 11.338L7.05746 11.2169Z" fill="#004D9D"/>
								</svg>
							</div>


							<div class="accordion__icon accordion__icon--arrow u-transition-all u-duration-300" data-expandable-btn-arrow>
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M18 9L11.9341 15L6 9" stroke="#004D9D" stroke-linecap="round" stroke-linejoin="round"/>
								</svg>
							</div>
						</div>
          </button>
          <div
            id='faq-item-${i}'
            class='c-expandable__wrapper | u-px-20 u-pb-20 lg:u-pb-30'
            data-expandable-wrapper
            style='display: none'
            aria-hidden='true'
						aria-labelledby='expandable-btn-${i}'
						role='region'
          >
            <div class='t-text'>
              ${answer}
            </div>
          </div>
        </article>`;

			const iconLink = faqElement.querySelector('[data-link-container]');
			const linkID = iconLink.id;
			const linkURL = iconLink.baseURI;
			const newID = `${linkURL}#${linkID}`;
			const copyMessage = iconLink.querySelector('[data-link-message]');
			iconLink.id = linkID;

			iconLink.addEventListener('click', () =>
				this.copyIdToClipboard(iconLink, copyMessage),
			);

			this.openCloseQuestions(faqElement);

			// Ajoute l'élément de catégorie à categoryContainer
			this.faqContainer.appendChild(faqElement);
		});
	}

	copyIdToClipboard(element, copyMessage) {
		const currentUrl = window.location.href.split('#')[0]; // Récupère l'URL sans le hash actuel

		const idValue = element.id;

		// Crée la nouvelle URL avec le nouvel ID
		const newUrl = `${currentUrl}#${idValue.replace(/.*#/, '')}`;

		// Copie la nouvelle URL dans le presse-papiers
		navigator.clipboard
			.writeText(newUrl)
			.then(() => {
				copyMessage.style.display = 'block';
				setTimeout(() => {
					copyMessage.style.display = 'none';
				}, 3000);
			})
			.catch((err) => {
				console.error(
					"Erreur lors de la copie de l'URL dans le presse-papiers :",
					err,
				);
			});
	}

	toUrlParameter(phrase) {
		return phrase
			.toLowerCase() // Convert to lowercase
			.replace(/[^\w\s-éèêëàâîïôûùçœæ]/g, '') // Remove all non-word chars except specified
			.replace(/[^A-Za-z0-9\s-]/g, '') // Remove all non alphanumeric and -
			.trim() // Remove leading/trailing spaces
			.replace(/\s+/g, '-') // Replace spaces with -
			.replace(/-+/g, '-'); // Replace multiple - with single -
	}

	renderResults(searchResults, count) {
		// Récupér le headerText avec les résultats de la recherche
		let headerText = this.resultsMessage
			.replace('{{ count }}', count)
			.replace('{{ terms }}', `"${this.searchTerms}"` || '""');

		// Trouvez le h3 dans le conteneur FAQ
		let resultText = this.faqContainer.querySelector(
			'.faq-page__search-results-count',
		);

		// Si le h3 existe déjà, mettez simplement à jour son contenu textuel
		if (resultText) {
			resultText.textContent = headerText;
			resultText.setAttribute('data-result', count);
		} else {
			// Sinon, créez un nouveau h3 et insérez-le au début du conteneur FAQ
			resultText = document.createElement('h3');
			resultText.setAttribute('data-result', count);
			resultText.classList.add(
				't-title',
				't-title--h3',
				'faq-page__search-results-count',
				'u-text-blue',
			);

			// if (count === 0) {
			// 	resultText.setAttribute('data-result', 0);
			// }

			resultText.textContent = headerText;
			this.faqContainer.insertBefore(resultText, this.faqContainer.firstChild);
		}
	}

	onSearchResultsChanged(count) {
		if (this.searchTerms) {
			this.renderResults(this.searchTerms, count);
		} else {
			this.renderFAQs();
		}
	}

	isSearching() {
		// Récupérer la valeur de l'input
		const searchTerm = this.searchInput.value.trim().toLowerCase();
		// Convertir NodeList en tableau
		const questionsToArray = Array.from(
			this.faqContainer.querySelectorAll('[data-category]'),
		);

		// Si >= à 3 chars tapés
		if (this.searchInput.value.length >= 1) {
			// Filtrer les questions en fonction de la valeur de l'input
			const filteredQuestions = questionsToArray.filter((question) => {
				const datasetQuestion = question.dataset.questionAnswer.toLowerCase();
				return datasetQuestion.includes(searchTerm);
			});

			// Afficher ou masquer les questions filtrées
			questionsToArray.forEach((question) => {
				if (filteredQuestions.includes(question)) {
					this.styleShowQuestion(question);
				} else {
					this.styleHideQuestion(question);
				}
			});

			this.searchTerms = this.searchInput.value;
			this.onSearchResultsChanged(filteredQuestions.length);

			this.el.classList.add('faq-page--searching');
			this.categoryWrapper.style.clipPath = 'inset(0 100% 0 0)';
			this.categoryWrapper.classList.add('u-w-0', 'u-h-0');
			this.container.classList.remove('u-grid-cols-[3fr_9fr]');
			this.container.classList.add('lg:u-grid-cols-[3fr_9fr]');
			if (this.faqContainer.querySelector('h3')) {
				this.faqContainer.querySelector('h3').style.display = 'block';
			}
		} else {
			if (this.faqContainer.querySelector('h3')) {
				this.faqContainer.querySelector('h3').style.display = 'none';
			}
			this.el.classList.remove('faq-page--searching');
			this.categoryWrapper.style.clipPath = 'inset(0 0 0 0)';
			this.categoryWrapper.classList.remove('u-w-0', 'u-h-0');
			this.container.classList.add('lg:u-grid-cols-[3fr_9fr]');
			this.container.classList.remove('u-grid-cols-[3fr_9fr]');

			// Afficher ou masquer les questions filtrées
			questionsToArray.forEach((question) => {
				this.styleShowQuestion(question);
			});
		}
	}

	styleHideQuestion(el) {
		el.classList.add('u-scale-0', 'u-absolute', 'u-top-0', 'u-opacity-0');
		el.querySelector('.c-expandable__btn').setAttribute('tabindex', '-1');
	}
	styleShowQuestion(el) {
		el.classList.remove('u-scale-0', 'u-absolute', 'u-top-0', 'u-opacity-0');
		el.querySelector('.c-expandable__btn').setAttribute('tabindex', '0');
	}

	openQuestion(faqElement) {
		const faqElWrapper = faqElement.querySelector('[data-expandable-wrapper]');
		const faqElBtn = faqElement.querySelector('[data-expandable-btn]');

		if (faqElBtn && faqElWrapper) {
			faqElBtn.setAttribute('aria-expanded', 'true');
			faqElWrapper.style.display = 'block';
			faqElWrapper.setAttribute('aria-hidden', 'false');
		}
	}

	openCloseQuestions(faqElement) {
		const faqElWrapper = faqElement.querySelector('[data-expandable-wrapper]');
		const faqElBtn = faqElement.querySelector('[data-expandable-btn]');
		const faqElArrow = faqElement.querySelector('[data-expandable-btn-arrow]');

		faqElBtn.addEventListener('click', () => {
			const currentIsOpen = faqElBtn.getAttribute('aria-expanded') === 'true';

			// Fermer toutes les questions sauf celle sur laquelle vous avez cliqué
			this.faqContainer
				.querySelectorAll('[data-expandable-btn]')
				.forEach((btn) => {
					if (btn !== faqElBtn) {
						btn.setAttribute('aria-expanded', 'false');
						btn
							.querySelector('[data-expandable-btn-arrow]')
							.classList.remove('u-rotate-180');
						btn.parentNode.querySelector(
							'[data-expandable-wrapper]',
						).style.display = 'none';
						btn.parentNode
							.querySelector('[data-expandable-wrapper]')
							.setAttribute('aria-hidden', 'true');
					}
				});

			// Mettre à jour l'état isOpen de la question sur laquelle vous avez cliqué
			faqElBtn.setAttribute('aria-expanded', currentIsOpen ? 'false' : 'true');
			faqElArrow.classList.toggle('u-rotate-180');
			faqElWrapper.style.display = currentIsOpen ? 'none' : 'block';
			faqElWrapper.setAttribute(
				'aria-hidden',
				currentIsOpen ? 'true' : 'false',
			);
		});
	}

	clickOnActiveCategory(categoryElement) {
		// active == true;
		categoryElement.classList.add('t-link', 'u-w-fit');

		// Fermer toutes les questions sauf celle sur laquelle vous avez cliqué
		this.categoryContainer.querySelectorAll('li').forEach((cat) => {
			if (cat !== categoryElement) {
				cat.classList.remove('t-link');
				cat.querySelector('span').classList.remove('t-link');
			}
		});

		// Convertir NodeList en tableau
		const faqs = Array.from(
			this.faqContainer.querySelectorAll('li.faq-page__question'),
		);

		const faqThatDontMatch = faqs.filter(
			(faq) => faq.dataset.category !== categoryElement.dataset.category,
		);
		const faqThatMatch = faqs.filter(
			(faq) => faq.dataset.category === categoryElement.dataset.category,
		);

		faqThatDontMatch.forEach((faq) => {
			this.styleHideQuestion(faq);
		});
		faqThatMatch.forEach((faq) => {
			this.styleShowQuestion(faq);
		});
	}

	// On ready show only the first category : (your-account)
	onReadyShowOnlyFirstCategory() {
		const questionsToArray = Array.from(
			this.faqContainer.querySelectorAll('[data-category]'),
		);

		const categoriesToArray = Array.from(
			this.categoryContainer.querySelectorAll('[data-category]'),
		);

		// get the first category to display this questions
		const firstCategory = categoriesToArray[0].dataset.category;

		// Filtrer les questions en fonction de la valeur de l'input
		const filteredQuestions = questionsToArray.filter((question) => {
			const datasetQuestion = question.dataset.category.toLowerCase();
			return datasetQuestion.includes(firstCategory);
		});

		// Afficher ou masquer les questions filtrées
		questionsToArray.forEach((question) => {
			if (filteredQuestions.includes(question)) {
				this.styleShowQuestion(question);
			} else {
				this.styleHideQuestion(question);
			}
		});
	}

	// ouvrir la category / question qui correspond à l'ancre de l'url.
	urlOpensQuestionCategory() {
		// Pour ouvrir automatiquement la question si l'ID est dans l'URL...
		const currentUrl = new URL(window.location.href);
		const hashId = currentUrl.hash.substring(1); // recup l'ID après le #

		if (hashId) {
			const targetElement = this.el.querySelector(`#${hashId}`);
			if (targetElement) {
				const faqElement = targetElement.closest('[data-question]');

				if (faqElement) {
					// Convertir NodeList en tableau
					const faqs = Array.from(
						this.faqContainer.querySelectorAll('li.faq-page__question'),
					);

					const faqThatDontMatch = faqs.filter(
						(faq) => faq.dataset.category !== faqElement.dataset.category,
					);
					const faqThatMatch = faqs.filter(
						(faq) => faq.dataset.category === faqElement.dataset.category,
					);

					faqThatDontMatch.forEach((faq) => {
						this.styleHideQuestion(faq);
					});
					faqThatMatch.forEach((faq) => {
						this.styleShowQuestion(faq);
					});
					this.openQuestion(faqElement);

					// Fermer toutes les questions sauf celles qui correspondent à la category de la question de l'url
					this.categoryContainer.querySelectorAll('li').forEach((cat) => {
						if (cat.dataset.category === faqElement.dataset.category) {
							cat.querySelector('span').classList.add('t-link');
						} else {
							cat.classList.remove('t-link');
							cat.querySelector('span').classList.remove('t-link');
						}
					});
				}
			}
		}
	}

	// styleHideQuestion(el) {
	// 	el.classList.add('u-scale-0', 'u-absolute', 'u-top-0', 'u-opacity-0');
	// }
	// styleShowQuestion(el) {
	// 	el.classList.remove('u-scale-0', 'u-absolute', 'u-top-0', 'u-opacity-0');
	// }

	//Keep this one temporaly : old behavior
	// initFaqJson() {
	// 	if (this.faqDataJson) {
	// 		// Parser la chaîne JSON en tant qu'objet JavaScript
	// 		const faqData = JSON.parse(this.faqDataJson);

	// 		// Initialisez this.faqData comme un tableau vide pour stocker les données de FAQ
	// 		this.faqData = [];

	// 		// Parcourir chaque élément de FAQ dans l'objet faqData et le stocker dans this.faqData sous forme d'objet
	// 		faqData.forEach((faqItem) => {
	// 			const [category, question, answer] = faqItem.split('|');
	// 			this.faqData.push({
	// 				category,
	// 				question,
	// 				answer,
	// 			});
	// 		});
	// 	} else {
	// 		console.error('Invalid value for data-faq-json attribute.');
	// 	}
	// }

	//Keep this one temporaly : old behavior
	// renderCategories() {
	// 	// Get the reviews from the data object
	// 	const categories = this.faqData;

	// 	// Vide le contenu de categoryContainer avant d'itérer sur faqData
	// 	this.categoryContainer.innerHTML = '';
	// 	const addedCategories = new Set();

	// 	categories.forEach((faqItem, index, active = false) => {
	// 		const {category} = faqItem;

	// 		// Check if the category has already been added
	// 		if (!addedCategories.has(category)) {
	// 			// Add the category to the set of added categories
	// 			addedCategories.add(category);
	// 			// Crée un élément de catégorie pour chaque élément de faqData
	// 			const categoryElement = document.createElement('li');
	// 			categoryElement.classList.add('u-cursor-pointer');
	// 			if (index == 0) {
	// 				categoryElement.classList.add(
	// 					'faq-page__category--active',
	// 					't-link',
	// 					'u-w-fit',
	// 				);
	// 			}

	// 			categoryElement.setAttribute(
	// 				'data-category',
	// 				category.replaceAll(' ', '-').toLowerCase(),
	// 			);
	// 			categoryElement.innerHTML = `
	//           <span href="${category
	// 						.replaceAll(' ', '-')
	// 						.toLowerCase()}" class="faq-page__category-title t-text t-text--14 ">${category}</span>
	//         `;

	// 			this.clickOnActiveCategory(categoryElement);

	// 			// Ajoute l'élément de catégorie à categoryContainer
	// 			this.categoryContainer.appendChild(categoryElement);
	// 		}
	// 	});
	// }
}
